import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Star, Send, CheckCircle2, Sparkles, Star as StarIcon, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Feedback() {
    const { caseNumber: paramCN } = useParams();
    const [caseNumber, setCaseNumber] = useState(paramCN || "");
    const [caseInfo, setCaseInfo] = useState(null);
    const [loadingCase, setLoadingCase] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    // Form state
    const [nps, setNps] = useState(null);
    const [fit, setFit] = useState(0);
    const [aesthetics, setAesthetics] = useState(0);
    const [delivery, setDelivery] = useState(0);
    const [comment, setComment] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("clinic");
    const [consentPublic, setConsentPublic] = useState(true);

    useEffect(() => {
        if (paramCN) lookup(paramCN);
         
    }, [paramCN]);

    const lookup = async (cn) => {
        if (!cn) return;
        setLoadingCase(true);
        try {
            const r = await axios.get(`${API}/feedback/case/${encodeURIComponent(cn.trim())}`);
            setCaseInfo(r.data);
            setCaseNumber(r.data.case_number);
        } catch (e) {
            setCaseInfo(null);
            toast.error("Case not found. Check the case number.");
        } finally {
            setLoadingCase(false);
        }
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (nps === null) { toast.error("Please pick a score from 0 to 10."); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API}/feedback`, {
                case_number: caseNumber,
                nps,
                fit_rating: fit || null,
                aesthetics_rating: aesthetics || null,
                delivery_rating: delivery || null,
                comment: comment.trim() || null,
                name: name.trim() || null,
                role,
                consent_public: consentPublic && (comment.trim().length > 0),
            });
            setDone(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        const isPromoter = nps !== null && nps >= 9;
        const reviewText = `Just had a 5-star experience with Toothrocket Labs — precision 3D-printed nano-ceramic restorations with a 10-year warranty. Highly recommend.`;
        const googleReview = "https://search.google.com/local/writereview?placeid=Toothrocket+Labs"; // generic — wire real Place ID once available
        const waShare = `https://wa.me/?text=${encodeURIComponent(reviewText + " https://toothrocket.com")}`;
        const mailShare = `mailto:?subject=${encodeURIComponent("Toothrocket Labs — worth a look")}&body=${encodeURIComponent(reviewText + "\n\nhttps://toothrocket.com")}`;
        return (
            <div data-testid="feedback-thanks" className="min-h-[70vh] flex items-center justify-center px-5 py-16">
                <div className="text-center max-w-2xl">
                    <CheckCircle2 size={48} className="text-emerald-500 mx-auto" strokeWidth={1.5} />
                    <div className="tr-overline mt-6 text-cyan-700">· Feedback received</div>
                    <h1 className="mt-3 font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900">
                        Thank you.<br />
                        <span className="italic text-slate-500">We read every word.</span>
                    </h1>
                    <p className="mt-5 text-slate-600 text-[15px] leading-relaxed">
                        Your feedback drives every iteration of our nano-ceramic resins and printer calibration.
                        If you flagged a concern, our lab lead will personally follow up within 24 hours.
                    </p>

                    {isPromoter && (
                        <div data-testid="feedback-share-promoter" className="mt-12 rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50/80 via-white to-white p-8 lg:p-10 text-left">
                            <div className="flex items-center gap-2">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <StarIcon key={i} size={20} className="text-amber-500 fill-amber-500" />
                                ))}
                                <span className="ml-2 text-[11.5px] font-mono-tr tracking-[0.22em] uppercase text-cyan-700">· Five stars · You're a promoter</span>
                            </div>
                            <h2 className="mt-4 font-display font-light text-2xl lg:text-3xl tracking-tight text-slate-900 leading-tight">
                                Would you tell a colleague?<br />
                                <span className="italic text-slate-500">One click, big impact.</span>
                            </h2>
                            <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed max-w-lg">
                                Word-of-mouth from clinicians like you is how we grow. Share a review or
                                forward Toothrocket to a colleague who'd benefit from a 10-year warranted,
                                3D-printed crown.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <a
                                    href={googleReview}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="share-google-review"
                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0B1120] text-white font-medium text-[13.5px] hover:bg-slate-800 transition"
                                >
                                    <StarIcon size={16} className="text-amber-400 fill-amber-400" />
                                    Leave a Google review
                                </a>
                                <a
                                    href={waShare}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="share-whatsapp"
                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-medium text-[13.5px] hover:opacity-90 transition"
                                >
                                    <MessageCircle size={16} />
                                    Share on WhatsApp
                                </a>
                                <a
                                    href={mailShare}
                                    data-testid="share-email"
                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-slate-800 font-medium text-[13.5px] hover:bg-slate-50 transition"
                                >
                                    <Mail size={16} />
                                    Email a colleague
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div data-testid="feedback-page" className="bg-gradient-to-b from-slate-50 to-white min-h-[80vh]">
            <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
                <div className="tr-overline text-cyan-700 mb-3">· Patient & Clinic feedback</div>
                <h1 className="font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
                    How did we do?
                </h1>
                <p className="mt-4 text-slate-600 text-[15.5px] leading-[1.8] max-w-xl">
                    30 seconds of your time helps us hold every Toothrocket restoration to the same standard.
                    Promoters help us grow; detractors are followed up by our lab lead within 24 hours.
                </p>

                {/* Case lookup */}
                {!caseInfo && (
                    <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
                        <label className="block text-[12px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-2">Case number</label>
                        <div className="flex gap-2">
                            <input
                                data-testid="feedback-case-input"
                                value={caseNumber}
                                onChange={(e) => setCaseNumber(e.target.value)}
                                placeholder="TR-2026-0001"
                                className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[15px] font-mono-tr"
                            />
                            <button
                                data-testid="feedback-case-lookup"
                                onClick={() => lookup(caseNumber)}
                                disabled={loadingCase || !caseNumber.trim()}
                                className="tr-btn !py-3"
                            >
                                {loadingCase ? <Loader2 size={16} className="animate-spin" /> : "Find case"}
                            </button>
                        </div>
                        <p className="mt-3 text-[12.5px] text-slate-500">
                            Find your case number on the delivery slip or the email/WhatsApp we sent.
                        </p>
                    </div>
                )}

                {caseInfo && (
                    <form data-testid="feedback-form" onSubmit={submit} className="mt-10 space-y-6">
                        {/* Case ribbon */}
                        <div className="rounded-2xl bg-[#0B1120] text-white px-6 py-5 flex items-center gap-4">
                            <Sparkles size={18} className="text-cyan-300" />
                            <div className="flex-1 min-w-0">
                                <div className="text-[10.5px] font-mono-tr tracking-[0.22em] uppercase text-cyan-300">· Case</div>
                                <div className="font-display text-xl tracking-tight truncate">{caseInfo.case_number}</div>
                                <div className="text-white/60 text-[12.5px]">{caseInfo.product} · {caseInfo.clinic_name}</div>
                            </div>
                        </div>

                        {/* NPS */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-6">
                            <div className="font-display text-lg tracking-tight text-slate-900">
                                How likely are you to recommend Toothrocket to a colleague?
                            </div>
                            <div className="mt-1 text-[13px] text-slate-500">0 = not likely · 10 = absolutely</div>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                                    <button
                                        type="button"
                                        key={n}
                                        data-testid={`feedback-nps-${n}`}
                                        onClick={() => setNps(n)}
                                        className={`h-11 w-11 rounded-xl border text-[14px] font-mono-tr transition ${
                                            nps === n
                                                ? "bg-cyan-600 border-cyan-600 text-white shadow-md shadow-cyan-900/20"
                                                : "bg-white border-slate-200 text-slate-700 hover:border-cyan-300 hover:bg-cyan-50"
                                        }`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Sub-ratings */}
                        <div className="grid sm:grid-cols-3 gap-4">
                            <RatingBlock label="Fit" value={fit} onChange={setFit} testid="rating-fit" />
                            <RatingBlock label="Aesthetics" value={aesthetics} onChange={setAesthetics} testid="rating-aesthetics" />
                            <RatingBlock label="Delivery speed" value={delivery} onChange={setDelivery} testid="rating-delivery" />
                        </div>

                        {/* Comment */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-6">
                            <label className="block text-[12px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-2">Comments (optional)</label>
                            <textarea
                                data-testid="feedback-comment"
                                rows={4}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Anything we should celebrate or fix?"
                                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14.5px] leading-relaxed resize-y"
                            />
                        </div>

                        {/* Identity */}
                        <div className="grid sm:grid-cols-3 gap-4">
                            <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
                                <label className="block text-[12px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-2">Your name (optional)</label>
                                <input
                                    data-testid="feedback-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Dr. Aman Khanna"
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14.5px]"
                                />
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-6">
                                <label className="block text-[12px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-2">Submitting as</label>
                                <select
                                    data-testid="feedback-role"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-[14.5px]"
                                >
                                    <option value="clinic">Clinic / Dentist</option>
                                    <option value="patient">Patient</option>
                                </select>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-cyan-200 bg-cyan-50/40 p-5 flex items-start gap-3">
                            <input
                                type="checkbox"
                                id="consent-public"
                                data-testid="feedback-consent-public"
                                checked={consentPublic}
                                onChange={(e) => setConsentPublic(e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-cyan-300 text-cyan-600 focus:ring-cyan-400"
                            />
                            <label htmlFor="consent-public" className="text-[13.5px] text-slate-700 leading-relaxed cursor-pointer">
                                <strong className="text-slate-900">Permission to share publicly.</strong> If I leave a positive comment,
                                Toothrocket may feature it on the public reviews wall — anonymously (only first name initial,
                                clinic redacted). Uncheck to keep feedback private.
                            </label>
                        </div>

                        <button
                            type="submit"
                            data-testid="feedback-submit"
                            disabled={submitting || nps === null}
                            className="tr-btn !py-3 !px-6 w-full sm:w-auto"
                        >
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /> Submit feedback</>}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function RatingBlock({ label, value, onChange, testid }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-[12px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-3">{label}</div>
            <div className="flex gap-1.5" data-testid={testid}>
                {[1, 2, 3, 4, 5].map((i) => (
                    <button
                        type="button"
                        key={i}
                        onClick={() => onChange(i === value ? 0 : i)}
                        className="p-1"
                        aria-label={`${label} ${i}`}
                    >
                        <Star size={26} className={i <= value ? "text-amber-500 fill-amber-500" : "text-slate-300 hover:text-amber-400"} />
                    </button>
                ))}
            </div>
        </div>
    );
}
